import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from "@/utils/env";
import { BRAND } from "@/brand";

const people = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/1.png";
import Navbar from "@/component/navbar";
import 'react-phone-number-input/style.css'
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input'
import CustomCountrySelect from '@/components/CustomCountrySelect'


import { Eye, EyeOff } from "lucide-react";

function Msignup() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // const [countryCode, setCountryCode] = useState("+971"); // Managed by PhoneInput now
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation
    if (!firstName || !lastName || !email || !password || !phoneNumber) {
      setError(t('signup.fillAllFields'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('signup.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('signup.passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      // Parse phone number to split country code and national number
      let parsedCountryCode = '';
      let parsedNationalNumber = '';

      if (phoneNumber) {
        try {
          const parsed = parsePhoneNumber(phoneNumber);
          if (parsed) {
            parsedCountryCode = `+${parsed.countryCallingCode}`;
            parsedNationalNumber = parsed.nationalNumber;
          } else {
            parsedCountryCode = '+971';
            parsedNationalNumber = phoneNumber;
          }
        } catch (err) {
          console.warn("Phone parsing error", err);
          parsedCountryCode = '+971';
          parsedNationalNumber = phoneNumber;
        }
      }

      // Create user with Backend API
      console.log('🔐 Attempting signup to:', `${API_BASE_URL}/auth/signup`);
      console.log('📧 Email:', email);
      console.log('📱 Phone:', `${parsedCountryCode}${parsedNationalNumber}`);

      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          phoneNumber: parsedNationalNumber,
          countryCode: parsedCountryCode,
          brand: BRAND
        })
      });

      console.log('📡 Signup response status:', response.status);

      const data = await response.json();
      console.log('📦 Signup response data:', data);

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create account');
      }

      // Store user info in localStorage
      localStorage.setItem("user", JSON.stringify({
        uid: data.user.uid,
        email: data.user.email,
        displayName: data.user.displayName,
        firstName,
        lastName
      }));

      if (data.tokens) {
        localStorage.setItem("authTokens", JSON.stringify(data.tokens));
        // persist individual token keys for compatibility with other flows
        try {
          if (data.tokens.idToken) {
            localStorage.setItem('idToken', data.tokens.idToken);
            localStorage.setItem('token', data.tokens.idToken);
          }
          if (data.tokens.refreshToken) {
            localStorage.setItem('refreshToken', data.tokens.refreshToken);
          }
        } catch (e) {
          console.warn('Could not persist tokens to localStorage', e);
        }
      }

      // Every email/password signup must verify their email first.
      // The post-verification destination is decided in EmailVerification.tsx
      // based on signupUserType (already stored in localStorage).
      navigate("/email-verification", { replace: true, state: { email: data.user.email, firstName } });

    } catch (err: any) {
      console.error("❌ Signup error:", err);
      console.error("Error details:", {
        message: err.message,
        stack: err.stack,
        name: err.name
      });

      // Network error
      if (err.message === "Failed to fetch" || err.name === "TypeError") {
        setError("Cannot connect to server. Please check your internet connection.");
      } else {
        setError(err.message || "Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse: CredentialResponse) => {
    // Validate credential exists
    if (!credentialResponse?.credential) {
      console.error('❌ No credential in Google response');
      setError("Failed to get Google credentials. Please try again.");
      return;
    }

    console.log('🔐 Google credential received, length:', credentialResponse.credential.length);
    setLoading(true);
    setError("");

    try {
      console.log('📤 Sending credential to backend:', `${API_BASE_URL}/auth/google-signin`);

      const response = await fetch(`${API_BASE_URL}/auth/google-signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: credentialResponse.credential, brand: BRAND })
      });

      console.log('📥 Backend response status:', response.status);

      // Parse response
      let data;
      try {
        data = await response.json();
        console.log('📦 Backend response data:', {
          success: data.success,
          hasUser: !!data.user,
          hasTokens: !!data.tokens,
          uid: data.user?.uid,
          email: data.user?.email
        });
      } catch (parseError) {
        console.error('❌ Failed to parse backend response:', parseError);
        throw new Error('Server returned invalid response');
      }

      // Check for errors
      if (!response.ok || !data.success) {
        console.error('❌ Backend returned error:', data.error);
        throw new Error(data.error || 'Google signin failed');
      }

      // Validate response data
      console.log('🔍 Validating response data:', JSON.stringify(data, null, 2));

      if (!data.user || !data.user.uid || data.user.uid === 'error' || data.user.uid === 'undefined') {
        console.error('❌ Invalid UID received:', data.user?.uid);
        console.error('❌ Full user object:', data.user);
        throw new Error('Failed to get valid user ID from Google authentication');
      }

      if (!data.tokens || !data.tokens.idToken) {
        console.error('❌ No tokens in response');
        throw new Error('Failed to get authentication tokens');
      }

      console.log('✅ Valid response received');
      console.log('👤 User UID:', data.user.uid);
      console.log('📧 Email:', data.user.email);

      // Store user data
      const userDataToStore = {
        uid: data.user.uid,
        email: data.user.email,
        displayName: data.user.displayName || `${data.user.firstName} ${data.user.lastName}`,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        photoURL: data.user.photoURL || '',
        emailVerified: true
      };

      localStorage.setItem("user", JSON.stringify(userDataToStore));
      console.log('💾 User data stored');

      // Store tokens
      localStorage.setItem("authTokens", JSON.stringify(data.tokens));
      localStorage.setItem('idToken', data.tokens.idToken);
      localStorage.setItem('token', data.tokens.idToken);
      localStorage.setItem('refreshToken', data.tokens.refreshToken || '');
      console.log('🔑 Tokens stored');

      console.log('✅ Google signin successful!');
      // Google accounts are already email-verified, so skip the code step.
      const googleUserType = localStorage.getItem('signupUserType');
      if (googleUserType === 'setup') {
        navigate("/setup-service", { replace: true });
      } else {
        // Consulting / default → country selection, then the Readiness Journey.
        // Make the choice explicit so a Google signup that landed here without a
        // prior service selection is consistently treated as consulting (and lands
        // on the consulting dashboard) rather than inheriting a stale flag.
        localStorage.setItem('signupUserType', 'investor');
        localStorage.setItem('activeServiceView', 'consulting');
        navigate("/Countrysl", { replace: true });
      }

    } catch (err: any) {
      console.error("❌ Google sign-in error:", err);

      // User-friendly error messages
      let errorMessage = "Failed to sign in with Google. ";

      if (err.message.includes('valid user ID')) {
        errorMessage += "Please try again or use email/password signup.";
      } else if (err.message.includes('authentication tokens')) {
        errorMessage += "Authentication failed. Please try again.";
      } else if (err.message.includes('invalid response')) {
        errorMessage += "Server error. Please try again later.";
      } else if (err.message === 'Failed to fetch') {
        errorMessage = "Cannot connect to server. Please check your internet connection.";
      } else {
        errorMessage += err.message || "Please try again.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error("❌ Google Sign-In button error");
    setError("Google Sign-In is not properly configured. Please use email/password signup or try the test signin.");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <Navbar></Navbar>

      {/* Main Content */}
      <div className="flex flex-col flex-1 bg-white">
        <div className="flex flex-col md:flex-row w-full flex-1">
          {/* Left Illustration - hidden on mobile */}
          <div className="hidden md:flex w-1/2 min-h-[calc(100vh-80px)] bg-linear-to-br from-blue-50 to-blue-100 items-center justify-center px-16">
            <img src={people} alt="People Illustration" className="w-full h-auto max-w-lg object-contain rounded-b-[60px]" />
          </div>

          {/* Right Content */}
          <div className="w-full md:w-1/2 min-h-full flex flex-col justify-center px-6 md:px-16 py-10 md:py-0">
            <div className="max-w-lg w-full">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t('signup.yourJourney')}<br />{t('signup.startsHere')}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('signup.accessTools')}
              </p>

              <p className="text-sm text-gray-900 mb-6">
                {t('signup.haveAccount')} <a href="/login" className="text-[#1976D3] hover:underline">{t('signup.signIn')}</a>
              </p>

              <form onSubmit={handleSignup} className="space-y-4">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-[45px] text-sm">
                    {error}
                  </div>
                )}

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder={t('signup.firstName')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-md placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#1976D3] focus:border-transparent"
                  />
                  <input
                    type="text"
                    placeholder={t('signup.lastName')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-md placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#1976D3] focus:border-transparent"
                  />
                </div>

                {/* Phone Number with Flag */}
                <div className="relative phone-input-container">
                  <PhoneInput
                    placeholder={t('signup.phoneNumber')}
                    value={phoneNumber}
                    onChange={(value) => setPhoneNumber(value || "")}
                    defaultCountry="AE"
                    international
                    countryCallingCodeEditable={false}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-md bg-white focus-within:ring-2 focus-within:ring-[#1976D3] focus-within:border-transparent"
                    countrySelectComponent={CustomCountrySelect}
                    numberInputProps={{
                      className: "flex-1 bg-transparent border-none focus:ring-0 p-0 text-sm placeholder:text-gray-400 outline-none"
                    }}
                  />
                </div>

                {/* Email */}
                <input
                  type="email"
                  placeholder={t('signup.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-md placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#1976D3] focus:border-transparent"
                />

                {/* Password */}
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t('signup.password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-md placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#1976D3] focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Confirm Password */}
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t('signup.confirmPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 border border-gray-300 rounded-md placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#1976D3] focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="flex-1 px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    ← {t('signup.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-2.5 text-sm font-medium text-white bg-[#1976D3] rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('signup.creatingAccount') : `${t('signup.createAccount')} →`}
                  </button>
                </div>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-4 py-4 mt-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="text-sm text-gray-500 font-medium">Or</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Google Sign In */}
              <div className="w-full flex flex-col items-center mt-4">
                <div className="inline-block">
                  <GoogleLogin
                    onSuccess={handleGoogleSignIn}
                    onError={handleGoogleError}
                    text="signup_with"
                    size="large"
                    width="400"
                    theme="outline"
                    shape="rectangular"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Msignup;